import {
	all,
	select,
	takeEvery,
	takeLatest,
	takeLeading,
	fork,
	call,
	put,
} from 'redux-saga/effects'

// action
import { inquiryMgmtActions, configActions } from '@redux/store/index'

// api
import { inquiryMgmtApi, usersApi, ResType } from '@api'

// lib
import { Logger } from '@utils'
import { INQUIRY_PAGE_SIZE } from '@configs'

// types
import { Inquiry, InquiryType, InquiryCommentType, InquiryStatusLogType } from '@schemas'
import { InquiryMgmtStateType } from '@redux/types'

const apiGetInquiries = async (
	status: string,
	cursor: string,
	q: string,
): Promise<ResType | undefined> => {
	const query: { [key: string]: string | number } = {
		status,
		limit: INQUIRY_PAGE_SIZE,
	}
	if (cursor) query.cursor = cursor
	if (q) query.q = encodeURIComponent(q)
	const res: ResType | undefined = await inquiryMgmtApi.getInquiries({ query })
	return res
}

const apiGetInquiryById = async (inquiryId: string): Promise<ResType | undefined> => {
	const res: ResType | undefined = await inquiryMgmtApi.getInquiryById({
		params: { inquiryId },
	})
	return res
}

const apiGetComments = async (inquiryId: string): Promise<ResType | undefined> => {
	const res: ResType | undefined = await inquiryMgmtApi.getInquiryComments({
		params: { inquiryId },
	})
	return res
}

const apiGetStatusLog = async (inquiryId: string): Promise<ResType | undefined> => {
	const res: ResType | undefined = await inquiryMgmtApi.getInquiryStatusLog({
		params: { inquiryId },
	})
	return res
}

const apiGetUsers = async (): Promise<ResType | undefined> => {
	const res: ResType | undefined = await usersApi.getUsers({
		query: { includingDeletedUsersYn: '0' },
	})
	return res
}

const apiUpdateStatus = async (
	inquiryId: string,
	body: { status: string; note: string },
): Promise<ResType | undefined> => {
	const res: ResType | undefined = await inquiryMgmtApi.updateInquiryStatus({
		params: { inquiryId },
		body,
	})
	return res
}

const apiUpdateHandler = async (
	inquiryId: string,
	body: { handlerId: string },
): Promise<ResType | undefined> => {
	const res: ResType | undefined = await inquiryMgmtApi.updateInquiryHandler({
		params: { inquiryId },
		body,
	})
	return res
}

const apiAddComment = async (
	inquiryId: string,
	body: { body: string; isInternal: boolean },
): Promise<ResType | undefined> => {
	const res: ResType | undefined = await inquiryMgmtApi.addInquiryComment({
		params: { inquiryId },
		body,
	})
	return res
}

const apiDeleteComment = async (
	inquiryId: string,
	commentId: string,
): Promise<ResType | undefined> => {
	const res: ResType | undefined = await inquiryMgmtApi.deleteInquiryComment({
		params: { inquiryId, commentId },
	})
	return res
}

const apiDeleteInquiry = async (inquiryId: string): Promise<ResType | undefined> => {
	const res: ResType | undefined = await inquiryMgmtApi.deleteInquiry({
		params: { inquiryId },
	})
	return res
}

const toInquiryList = (res: ResType | undefined): InquiryType[] => {
	if (!res || res.status !== 200) return []
	const tempList = (res.data as any[]) ?? []
	return tempList.map(anItem => new Inquiry(anItem as InquiryType))
}

function* putUsers(usersRes: ResType | undefined) {
	if (usersRes && usersRes.status === 200) {
		const usersData = usersRes.data[0] as {
			users?: { id: string; name: string }[]
		}
		const users = usersData?.users ?? []
		yield put(inquiryMgmtActions.USER_LIST(users.map(u => ({ id: u.id, name: u.name }))))
	}
}

/** 첫 페이지. 필터가 바뀌거나 화면에 들어올 때. */
function* triggerInitInquiryMgmt() {
	try {
		yield put(inquiryMgmtActions.INQUIRY_LIST_STATUS('loading'))
		yield put(inquiryMgmtActions.HAS_MORE(false))
		yield put(inquiryMgmtActions.IS_LOADING_MORE(false))
		const statusFilter: string = yield select(state => state.inquiryMgmt.statusFilter)
		const searchQuery: string = yield select(state => state.inquiryMgmt.searchQuery)

		const [inquiryRes, usersRes]: [ResType, ResType] = yield all([
			call(apiGetInquiries, statusFilter, '', searchQuery),
			call(apiGetUsers),
		])

		// 기다리는 사이 탭·검색어가 바뀌었으면 버린다 (takeLatest 가 보통 막지만 방어)
		const nowFilter: string = yield select(state => state.inquiryMgmt.statusFilter)
		const nowQuery: string = yield select(state => state.inquiryMgmt.searchQuery)
		if (nowFilter !== statusFilter || nowQuery !== searchQuery) return

		const newList = toInquiryList(inquiryRes)
		yield put(inquiryMgmtActions.INQUIRY_LIST(newList))
		yield put(inquiryMgmtActions.HAS_MORE(newList.length >= INQUIRY_PAGE_SIZE))
		yield put(inquiryMgmtActions.INQUIRY_LIST_STATUS(newList.length > 0 ? 'ok' : 'empty'))

		yield* putUsers(usersRes)
	} catch (error) {
		Logger.warn(error)
	}
}

/** 다음 페이지 — 스크롤이 끝에 가까워지면. 마지막 행 id 가 커서. */
function* triggerLoadMoreInquiries() {
	try {
		const hasMore: boolean = yield select(state => state.inquiryMgmt.hasMore)
		const isLoadingMore: boolean = yield select(state => state.inquiryMgmt.isLoadingMore)
		const inquiryList: InquiryType[] = yield select(
			state => state.inquiryMgmt.inquiryList,
		)
		if (!hasMore || isLoadingMore || inquiryList.length === 0) return

		yield put(inquiryMgmtActions.IS_LOADING_MORE(true))
		const statusFilter: string = yield select(state => state.inquiryMgmt.statusFilter)
		const searchQuery: string = yield select(state => state.inquiryMgmt.searchQuery)
		const cursor = inquiryList[inquiryList.length - 1].id

		const inquiryRes: ResType | undefined = yield call(
			apiGetInquiries,
			statusFilter,
			cursor,
			searchQuery,
		)

		// 기다리는 사이 탭·검색어가 바뀌었으면 이 페이지는 버린다
		const nowFilter: string = yield select(state => state.inquiryMgmt.statusFilter)
		const nowQuery: string = yield select(state => state.inquiryMgmt.searchQuery)
		if (nowFilter !== statusFilter || nowQuery !== searchQuery) return

		const newList = toInquiryList(inquiryRes)
		yield put(inquiryMgmtActions.APPEND_INQUIRY_LIST(newList))
		yield put(inquiryMgmtActions.HAS_MORE(newList.length >= INQUIRY_PAGE_SIZE))
	} catch (error) {
		Logger.warn(error)
	} finally {
		yield put(inquiryMgmtActions.IS_LOADING_MORE(false))
	}
}

/** 드로어 대상 한 건의 최신 본문 + 코멘트 + 상태 이력. */
function* triggerLoadInquiryDetail() {
	try {
		const selectedInquiry: InquiryType | null = yield select(
			state => state.inquiryMgmt.selectedInquiry,
		)
		if (!selectedInquiry) {
			yield put(inquiryMgmtActions.SELECTED_INQUIRY_STATUS('empty'))
			return
		}
		yield put(inquiryMgmtActions.SELECTED_INQUIRY_STATUS('loading'))
		const inquiryId = selectedInquiry.id

		const userList: { id: string; name: string }[] = yield select(
			state => state.inquiryMgmt.userList,
		)

		const effects: any[] = [
			call(apiGetInquiryById, inquiryId),
			call(apiGetComments, inquiryId),
			call(apiGetStatusLog, inquiryId),
		]
		if (userList.length === 0) {
			effects.push(call(apiGetUsers))
		}

		const results: ResType[] = yield all(effects)
		const [detailRes, commentsRes, statusLogRes, usersRes] = results

		// 기다리는 사이 다른 카드로 옮겨갔으면 버린다 (takeLatest 가 보통 막지만 방어)
		const nowSelected: InquiryType | null = yield select(
			state => state.inquiryMgmt.selectedInquiry,
		)
		if (!nowSelected || nowSelected.id !== inquiryId) return

		const [inquiryData] = toInquiryList(detailRes)
		if (inquiryData) {
			// 목록 카드도 같은 값으로 맞춘다 (상태·담당자·첨부)
			yield put(inquiryMgmtActions.PATCH_INQUIRY({ id: inquiryId, changes: inquiryData }))
			yield put(inquiryMgmtActions.SELECTED_INQUIRY(inquiryData))
			yield put(inquiryMgmtActions.SELECTED_INQUIRY_STATUS('ok'))
		} else {
			yield put(inquiryMgmtActions.SELECTED_INQUIRY_STATUS('empty'))
		}

		if (commentsRes && commentsRes.status === 200) {
			const comments = (commentsRes.data as unknown as InquiryCommentType[]) ?? []
			yield put(inquiryMgmtActions.COMMENTS(comments))
		}

		if (statusLogRes && statusLogRes.status === 200) {
			const logs = (statusLogRes.data as unknown as InquiryStatusLogType[]) ?? []
			yield put(inquiryMgmtActions.STATUS_LOGS(logs))
		}

		yield* putUsers(usersRes)
	} catch (error) {
		Logger.warn(error)
	}
}

/** 드로어가 이 문의를 보고 있으면 타임라인을 다시 읽는다. */
function* reloadDetailIfSelected(inquiryId: string) {
	const selectedInquiry: InquiryType | null = yield select(
		state => state.inquiryMgmt.selectedInquiry,
	)
	if (selectedInquiry && selectedInquiry.id === inquiryId) {
		yield put(inquiryMgmtActions.TRIGGER_LOAD_INQUIRY_DETAIL(true))
	}
}

function* triggerUpdateStatus() {
	try {
		const payload: InquiryMgmtStateType['updateStatusPayload'] = yield select(
			state => state.inquiryMgmt.updateStatusPayload,
		)
		const res: ResType = yield call(apiUpdateStatus, payload.inquiryId, {
			status: payload.status,
			note: payload.note,
		})
		if (res && (res.status === 200 || res.status === 201)) {
			yield put(configActions.TOAST({ isShow: true, message: '상태가 변경되었습니다.' }))

			// 목록은 다시 받지 않고 그 행만 고친다. 필터 탭과 어긋나면 목록에서 뺀다.
			const statusFilter: string = yield select(state => state.inquiryMgmt.statusFilter)
			if (statusFilter !== 'all' && statusFilter !== payload.status) {
				yield put(inquiryMgmtActions.REMOVE_INQUIRY(payload.inquiryId))
			} else {
				yield put(
					inquiryMgmtActions.PATCH_INQUIRY({
						id: payload.inquiryId,
						changes: { status: payload.status },
					}),
				)
			}
			yield* reloadDetailIfSelected(payload.inquiryId)
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* triggerUpdateHandler() {
	try {
		const payload: InquiryMgmtStateType['updateHandlerPayload'] = yield select(
			state => state.inquiryMgmt.updateHandlerPayload,
		)
		const res: ResType = yield call(apiUpdateHandler, payload.inquiryId, {
			handlerId: payload.handlerId,
		})
		if (res && (res.status === 200 || res.status === 201)) {
			yield put(
				configActions.TOAST({ isShow: true, message: '담당자가 변경되었습니다.' }),
			)
			yield put(
				inquiryMgmtActions.PATCH_INQUIRY({
					id: payload.inquiryId,
					changes: { handlerId: payload.handlerId },
				}),
			)
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* triggerAddComment() {
	try {
		const payload: InquiryMgmtStateType['addCommentPayload'] = yield select(
			state => state.inquiryMgmt.addCommentPayload,
		)
		const res: ResType = yield call(apiAddComment, payload.inquiryId, {
			body: payload.body,
			isInternal: payload.isInternal,
		})
		if (res && (res.status === 200 || res.status === 201)) {
			yield* reloadDetailIfSelected(payload.inquiryId)
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* triggerDeleteComment() {
	try {
		const payload: InquiryMgmtStateType['deleteCommentPayload'] = yield select(
			state => state.inquiryMgmt.deleteCommentPayload,
		)
		const res: ResType = yield call(
			apiDeleteComment,
			payload.inquiryId,
			payload.commentId,
		)
		if (res && (res.status === 200 || res.status === 201)) {
			yield* reloadDetailIfSelected(payload.inquiryId)
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* triggerDeleteInquiry() {
	try {
		const inquiryId: string = yield select(state => state.inquiryMgmt.deleteInquiryId)
		const res: ResType = yield call(apiDeleteInquiry, inquiryId)
		if (res && (res.status === 200 || res.status === 201)) {
			yield put(configActions.TOAST({ isShow: true, message: '문의가 삭제되었습니다.' }))
			yield put(inquiryMgmtActions.REMOVE_INQUIRY(inquiryId))

			const selectedInquiry: InquiryType | null = yield select(
				state => state.inquiryMgmt.selectedInquiry,
			)
			if (selectedInquiry && selectedInquiry.id === inquiryId) {
				yield put(inquiryMgmtActions.IS_TIMELINE_OPEN(false))
				yield put(inquiryMgmtActions.SELECTED_INQUIRY(null))
				yield put(inquiryMgmtActions.COMMENTS([]))
				yield put(inquiryMgmtActions.STATUS_LOGS([]))
			}
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* mainSaga() {
	yield takeLatest(inquiryMgmtActions.TRIGGER_INIT_INQUIRY_MGMT, triggerInitInquiryMgmt)
	yield takeLeading(
		inquiryMgmtActions.TRIGGER_LOAD_MORE_INQUIRIES,
		triggerLoadMoreInquiries,
	)
	yield takeLatest(
		inquiryMgmtActions.TRIGGER_LOAD_INQUIRY_DETAIL,
		triggerLoadInquiryDetail,
	)
	yield takeEvery(inquiryMgmtActions.TRIGGER_UPDATE_STATUS, triggerUpdateStatus)
	yield takeEvery(inquiryMgmtActions.TRIGGER_UPDATE_HANDLER, triggerUpdateHandler)
	yield takeEvery(inquiryMgmtActions.TRIGGER_ADD_COMMENT, triggerAddComment)
	yield takeEvery(inquiryMgmtActions.TRIGGER_DELETE_COMMENT, triggerDeleteComment)
	yield takeEvery(inquiryMgmtActions.TRIGGER_DELETE_INQUIRY, triggerDeleteInquiry)
}

export default function* rootSaga() {
	yield all([fork(mainSaga)])
}
